/**
 * Subscription tier definitions — single source of truth.
 * Architecture from: docs/BUSINESS_PLAN.md §3 Pricing Architecture
 *
 * Free  | $0    — basic charting, limited data, no AI research
 * Pro   | $49   — drawings, AI (2/day), SEC/earnings, options, backtesting, optimizer
 * Pro+  | $99   — unlimited AI, dark pool, supply chain, geo risk, options flow
 * Team  | $199  — all Pro+ + 3-5 seats, audit log, client reporting
 */

export type Plan = 'free' | 'pro' | 'pro_plus' | 'team'

export const PLAN_RANK: Record<Plan, number> = {
  free: 0,
  pro: 1,
  pro_plus: 2,
  team: 3,
}

/**
 * Extract the user's current plan from Supabase user_metadata.
 * Legacy 'enterprise' maps to 'pro_plus'; legacy 'team' maps to 'team'.
 */
export function userPlan(user: { user_metadata?: { plan?: string } } | null | undefined): Plan {
  const p = user?.user_metadata?.plan
  if (p === 'team') return 'team'
  if (p === 'pro_plus' || p === 'enterprise') return 'pro_plus'
  if (p === 'pro') return 'pro'
  return 'free'
}

/** True if the user's plan meets or exceeds minPlan. Founder passcode bypasses. */
export function hasAccess(
  user: { user_metadata?: { plan?: string } } | null | undefined,
  minPlan: Plan,
): boolean {
  if (localStorage.getItem('bb_pro_unlocked') === '1') return true
  return PLAN_RANK[userPlan(user)] >= PLAN_RANK[minPlan]
}

export interface TierFeature {
  label: string
  included: boolean
  note?: string
}

export interface Tier {
  id: Plan
  name: string
  price: string
  annualPrice?: string
  period: string
  highlight: boolean
  badge: string | null
  cta: string
  ctaHref: string
  description: string
  targetUser: string
  features: TierFeature[]
}

export const TIERS: Tier[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '/month',
    highlight: false,
    badge: null,
    cta: 'Get Started Free',
    ctaHref: '/login',
    description: 'Live data and core tools — no credit card required.',
    targetUser: 'Hobbyists, students, feature sampling',
    features: [
      // Charting
      { label: 'Charts + 10 core indicators', included: true },
      { label: 'Candlestick, line & area charts', included: true },
      { label: 'Pre/post-market quotes (1h window)', included: true },
      // Fundamentals
      { label: 'Real-time quotes & basic fundamentals', included: true },
      { label: 'News feed', included: true },
      { label: 'Analyst consensus', included: true },
      { label: 'Earnings calendar', included: true },
      // Screener
      { label: 'Screener — preset screens only', included: true },
      { label: 'Quant scores (V/G/Q/M) — view only', included: true },
      // Market Intel
      { label: 'Institutional ownership (basic)', included: true },
      { label: 'Insider trades (basic)', included: true },
      { label: 'Short interest (basic)', included: true },
      // Social & Macro
      { label: 'Social sentiment (StockTwits + Reddit WSB)', included: true },
      { label: 'Economic calendar', included: true },
      { label: 'Macro dashboard (basic)', included: true },
      // Watchlist
      { label: '1 watchlist (browser-only price alerts)', included: true },
      { label: 'Crypto dashboard', included: true },
      { label: 'Dividend tracker (basic)', included: true },
      { label: 'BYO API keys', included: true },
      // Locked
      { label: 'AI equity research', included: false },
      { label: 'Drawings, Fibonacci, trendlines', included: false },
      { label: 'Multi-symbol comparison', included: false },
      { label: 'Options chain (IV surface, Greeks)', included: false },
      { label: 'Strategy backtesting', included: false },
      { label: 'Portfolio optimizer', included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$49',
    annualPrice: '$39',
    period: '/month',
    highlight: true,
    badge: 'Most Popular',
    cta: 'Start Pro Trial',
    ctaHref: '/login',
    description: 'Professional charting, AI research, and advanced analytics.',
    targetUser: 'Serious retail traders, active investors',
    features: [
      { label: 'Everything in Free', included: true },
      { label: 'AI equity research — 2 analyses/day', included: true },
      { label: 'SEC 10-K AI reader (auto-loaded)', included: true },
      { label: 'Earnings transcripts (8-K summaries)', included: true },
      { label: '35+ technical indicators (full library)', included: true },
      { label: 'Drawings: Fibonacci, trendlines, H/S levels', included: true },
      { label: 'Custom Pine Script v5 indicator editor', included: true },
      { label: 'Multi-symbol comparison + correlation', included: true },
      { label: 'Extended hours (full pre/post market)', included: true },
      { label: 'Options chain: IV surface, Greeks, multi-leg', included: true, note: 'basic strategies' },
      { label: 'Strategy backtester (5yr history)', included: true },
      { label: 'Portfolio optimizer (MPT efficient frontier)', included: true },
      { label: 'Institutional ownership (full 13F)', included: true },
      { label: 'Congress trades tracker', included: true },
      { label: 'Screener — custom filters + CSV export', included: true },
      { label: 'Saved screener filters', included: true },
      { label: 'Unlimited watchlists + email price alerts', included: true },
      { label: 'AI watchlist scoring', included: true },
      { label: 'Equity research reports (AI)', included: true },
      { label: 'Risk intelligence dashboard', included: true },
      { label: 'Macro dashboard (full)', included: true },
      { label: 'Dividend safety scores + 5yr history', included: true },
      { label: 'API access — 10K calls/month', included: true },
      // Locked
      { label: 'Unlimited AI analyses', included: false },
      { label: 'Dark pool / short volume data', included: false },
      { label: 'Supply chain network maps', included: false },
      { label: 'Geopolitical risk heatmap', included: false },
    ],
  },
  {
    id: 'pro_plus',
    name: 'Pro+',
    price: '$99',
    annualPrice: '$79',
    period: '/month',
    highlight: false,
    badge: 'For Power Users',
    cta: 'Start Pro+ Trial',
    ctaHref: '/login',
    description: 'Unlimited AI + institutional-grade flow and intelligence data.',
    targetUser: 'Options traders, quants, small fund managers',
    features: [
      { label: 'Everything in Pro', included: true },
      { label: 'Unlimited AI analyses (Claude, GPT-4o, Gemini)', included: true },
      { label: 'Dark pool volume + short interest (FINRA)', included: true },
      { label: 'Options flow alerts + unusual activity feeds', included: true },
      { label: 'Advanced multi-leg options strategies', included: true, note: 'Iron Condor, Butterfly, Calendar, Strangle' },
      { label: 'Supply chain network maps (AI-generated)', included: true },
      { label: 'Geopolitical risk heatmap + impact scoring', included: true },
      { label: 'Sector rotation signals', included: true },
      { label: 'Webhook / Zapier integration (beta)', included: true },
      { label: 'API access — 100K calls/month', included: true },
      // Locked
      { label: 'Team seats (3–5)', included: false },
      { label: 'Client reporting', included: false },
      { label: 'Compliance audit log', included: false },
    ],
  },
  {
    id: 'team',
    name: 'Team',
    price: '$199',
    annualPrice: '$159',
    period: '/month',
    highlight: false,
    badge: 'RIA / Advisors',
    cta: 'Contact Sales',
    ctaHref: 'mailto:sales@quanttradeai.com',
    description: 'Full platform with team collaboration and compliance tools.',
    targetUser: 'RIAs, advisors, family offices (3–5 seats)',
    features: [
      { label: 'Everything in Pro+', included: true },
      { label: '3–5 team seats', included: true },
      { label: 'Shared watchlists & price alerts', included: true },
      { label: 'Client reporting (PDF export)', included: true },
      { label: 'Compliance audit log (SHA-256 hash-linked)', included: true },
      { label: 'Data provenance / trust graph', included: true },
      { label: 'AI artifact registry (audit all LLM outputs)', included: true },
      { label: 'Custom AI system prompts per seat', included: true },
      { label: 'Webhook / Zapier (full, production)', included: true },
      { label: 'Unlimited API calls', included: true },
      { label: 'Priority support (24h SLA)', included: true },
      { label: 'SSO / SAML (coming Q3 2026)', included: true, note: 'roadmap' },
    ],
  },
]

/** Feature / route → minimum plan required. */
export const FEATURE_PLAN_MAP: Record<string, Plan> = {
  // Pro-gated features
  'multi-chart': 'pro',
  'institutional': 'pro',
  'backtest': 'pro',
  'optimizer': 'pro',
  'options': 'pro',
  'sec': 'pro',
  'equity-research': 'pro',
  'risk-intelligence': 'pro',
  'congress': 'pro',
  // Pro+-gated features
  'supply-chain': 'pro_plus',
  'geopolitical': 'pro_plus',
  'dark-pool': 'pro_plus',
  'sector-rotation': 'pro_plus',
  // Team-gated features
  'audit': 'team',
  'client-reporting': 'team',
}

export const PLAN_LABELS: Record<Plan, string> = {
  free: 'Free',
  pro: 'Pro',
  pro_plus: 'Pro+',
  team: 'Team',
}

export const PLAN_COLORS: Record<Plan, string> = {
  free: 'text-bb-subtle',
  pro: 'text-bb-orange',
  pro_plus: 'text-bb-green',
  team: 'text-[#a78bfa]',
}

export const PLAN_BG_COLORS: Record<Plan, string> = {
  free: 'bg-bb-surface border-bb-border/50',
  pro: 'bg-bb-orange/10 border-bb-orange/30',
  pro_plus: 'bg-bb-green/10 border-bb-green/30',
  team: 'bg-[#a78bfa]/10 border-[#a78bfa]/30',
}
